import { useEffect, useState, useRef } from "react";
import { Loader2, Upload, Wrench } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Button } from "../../components/ui/button";
import api, { formatApiError } from "../../lib/api";

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const fileRef = useRef(null);

  const load = () => { setLoading(true); api.get("/admin/media").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const onFixUrls = async () => {
    if (!window.confirm("Scan the database and convert any preview/temporary image URLs to production-safe ones? This is safe to run anytime.")) return;
    setFixing(true);
    try {
      const { data } = await api.post("/admin/media/cleanup-urls");
      if (data.ok) {
        toast.success(`Image URLs fixed — ${data.documents_updated} record(s) updated, ${data.cdn_images_rehosted} image(s) re-hosted, 0 preview URLs left.`);
      } else {
        toast.warning(`Ran, but ${data.emergent_refs_remaining} reference(s) still remain. Check server logs.`);
      }
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setFixing(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post("/media/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="space-y-6" data-testid="admin-media">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-playfair text-2xl sm:text-3xl text-white">Media Library</h1>
          <p className="mt-2 text-muted-foreground">Uploaded images with optimised WebP + thumbnails. ZIP import lands in Phase 3.</p>
        </div>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUpload} data-testid="media-lib-file" />
        <div className="flex items-center gap-2">
          <Button data-testid="media-fix-urls" variant="outline" disabled={fixing} onClick={onFixUrls} className="gap-2 border-gold/40 text-gold hover:bg-gold/10">
            {fixing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />} Fix Image URLs
          </Button>
          <Button data-testid="media-lib-upload" disabled={uploading} onClick={() => fileRef.current?.click()} className="cf-gold-btn font-semibold gap-2">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>
      ) : items.length === 0 ? (
        <GlassCard className="p-10 text-center text-muted-foreground" hover={false}>No media uploaded yet.</GlassCard>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {items.map((m) => (
            <GlassCard key={m.media_id} className="p-2" data-testid={`media-lib-${m.media_id}`}>
              <img src={m.thumb_url || m.url} alt={m.alt} className="h-24 w-full object-cover rounded-lg" />
              <p className="mt-2 text-[11px] text-white/50 truncate">{m.original_filename}</p>
              <p className="text-[10px] text-white/30">{m.width}×{m.height}</p>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
}
